<script lang="ts">
	import { clone, create, equals } from "@bufbuild/protobuf";
	import { FieldMaskSchema } from "@bufbuild/protobuf/wkt";
	import { Alert, Input, Label, Radio } from "flowbite-svelte-v1";
	import { getContext } from "svelte";
	import { twMerge } from "tailwind-merge";
	import isURL from "validator/lib/isURL";
	import type { Workspace } from "#gen/identities/v1/identities_pb.js";
	import { PolicyContentSchema, PolicySchema, type Policy, type PolicyContent } from "#gen/legal/v1/legal_pb.js";
	import type { LegalClient } from "#lib/api/legal.js";
	import Icon from "#lib/components/Icon.svelte";
	import P from "#lib/components/P.svelte";
	import type { SuccessToastWithLinkInterface } from "#lib/components/SuccessToast.svelte";
	import HtmlEditor from "#lib/components/content-editors/HtmlEditor.svelte";
	import { shouldIgnoreShortcut } from "#lib/keyboard.js";
	import Button from "#lib/ui/Button/index.js";
	import { normalizeUrl } from "#lib/url.js";
	import { invalidate } from "$app/navigation";
	import type { Writable } from "svelte/store";

	interface Props {
		existingPolicy?: Policy;
		onClose: (policy?: Policy) => void;
		workspace?: Workspace;
		orgId: string;
		open: boolean;
		submitDisabled?: boolean;
		class?: string;
		isSubmitting?: boolean;
		showCreateAndUse?: boolean;
	}

	const maxPolicyContentCharCount = 50000;
	const legalClient: LegalClient = getContext("legalClient");
	const successMessage: Writable<SuccessToastWithLinkInterface | string | undefined> = getContext("successMessage");

	let { existingPolicy, onClose, workspace, orgId, open, submitDisabled = $bindable(false), class: className, isSubmitting = $bindable(false), showCreateAndUse = false }: Props = $props();

	let policyNameInput: HTMLInputElement | undefined = $state(undefined);
	const policy = $state<Policy>(newPolicy());
	let textInputCharCount = $derived(existingPolicy?.content?.content.case === "text" ? stripOutHtmlTags(existingPolicy.content?.content.value).length : 0);
	let lastError: unknown = $state(undefined);
	let policyType: "text" | "url" = $derived(existingPolicy?.content?.content.case ?? "text");
	let policyUrlContent = $derived(existingPolicy?.content?.content.case === "url" ? existingPolicy.content?.content.value : "");
	let policyHtmlContent = $derived(existingPolicy?.content?.content.case === "text" ? existingPolicy.content?.content.value : "");
	const disabled: boolean = $derived(workspace && existingPolicy ? existingPolicy.workspaceId !== workspace.id : false);

	// if an error message is displayed, this helps to keep the error message and the footer visible
	$effect(() => {
		if (lastError) {
			const el = document.getElementById("policy-editor-error");
			if (el) {
				el.scrollIntoView({ behavior: "smooth" });
			}
		}
	});

	$effect(() => {
		submitDisabled = disabled || !isPolicyChanged() || !validatePolicy(false);
	});

	$effect(() => {
		if (policyUrlContent) {
			handleUrlInput(policyUrlContent);
		}
	});

	$effect(() => {
		if (open) {
			if (existingPolicy) {
				policy.id = existingPolicy.id;
				policy.name = existingPolicy.name;
				policy.description = existingPolicy.description;
				policy.content = clone(PolicyContentSchema, existingPolicy.content!);
			} else if (!showCreateAndUse) {
				// In this case it is safe to autofocus. In the StackController context we need to wait for onIntroEnd.
				policyNameInput?.focus();
			}
		}
	});

	export function onIntroEnd() {
		policyNameInput?.focus();
	}

	export function createOrUpdatePolicy() {
		if (existingPolicy) {
			updatePolicy();
		} else {
			createPolicy();
		}
	}

	function stripOutHtmlTags(html: string): string {
		const div = document.createElement("div");
		div.innerHTML = html;
		return div.textContent || div.innerText || "";
	}

	function handleUrlInput(urlInput: string) {
		policyUrlContent = normalizeUrl(urlInput);
	}

	function handleTextContentChange(html: string, charCount?: number) {
		policyHtmlContent = html;

		if (charCount !== undefined) {
			textInputCharCount = charCount;
		}
	}

	function handleKeyDown(e: KeyboardEvent) {
		if (shouldIgnoreShortcut(e)) {
			return;
		}
		if (e.key === "Escape") {
			onClose();
		}
	}

	function newPolicy(): Policy {
		return create(PolicySchema, { content: create(PolicyContentSchema, { content: { case: "text", value: "" } }) });
	}

	async function updatePolicy() {
		lastError = undefined;
		if (!validatePolicy(true)) {
			return;
		}
		isSubmitting = true;
		try {
			policy.content = buildNewPolicyContent();
			const updatedPolicy = await legalClient.updatePolicy(
				policy,
				create(FieldMaskSchema, {
					paths: ["name", "description", "content"],
				}),
			);
			if (updatedPolicy) {
				await invalidate(url => url.pathname === "/legal.v1.LegalService/ListPolicies");
				$successMessage = `Policy "${policy.name}" was successfully updated!`;
				onClose(updatedPolicy);
			} else {
				lastError = `Failed to save policy`;
			}
		} catch (e) {
			lastError = `Failed to save policy: ${e}`;
		} finally {
			isSubmitting = false;
		}
	}

	async function createPolicy() {
		lastError = undefined;
		if (!validatePolicy(true)) {
			return;
		}
		isSubmitting = true;
		try {
			const createdPolicy = await legalClient.createPolicy(
				create(PolicySchema, {
					orgId: orgId,
					workspaceId: workspace?.id,
					name: policy.name,
					description: policy.description,
					content: buildNewPolicyContent(),
				}),
			);
			if (createdPolicy) {
				await invalidate(url => url.pathname === "/legal.v1.LegalService/ListPolicies");
				$successMessage = `Policy "${policy.name}" was successfully created!`;
				onClose(createdPolicy);
			} else {
				lastError = `Failed to create policy`;
			}
		} catch (e) {
			lastError = `Failed to create policy: ${e}`;
		} finally {
			isSubmitting = false;
		}
	}

	function isPolicyChanged(): boolean {
		if (!existingPolicy) return true;
		return existingPolicy.name !== policy.name || existingPolicy.description !== policy.description || !equals(PolicyContentSchema, existingPolicy.content!, buildNewPolicyContent());
	}

	function validatePolicy(fromSubmit: boolean = false): boolean {
		if (!policy.content?.content) {
			if (fromSubmit) lastError = "URL or text content is required.";
			return false;
		}

		switch (policyType) {
			case "url":
				try {
					if (!isURL(policyUrlContent, { require_protocol: true })) {
						if (fromSubmit) lastError = "Please enter a valid URL";
						return false;
					}
					return true;
				} catch (e) {
					if (fromSubmit) lastError = `Invalid URL: ${e}`;
					return false;
				}
			case "text":
				if (!stripOutHtmlTags(policyHtmlContent).length) {
					if (fromSubmit) lastError = "Text content cannot be empty.";
					return false;
				}
				return true;
			default:
				if (fromSubmit) lastError = "URL or text content is required.";
				return false;
		}
	}

	function buildNewPolicyContent(): PolicyContent {
		if (policyType === "text") {
			return create(PolicyContentSchema, { content: { case: "text", value: policyHtmlContent } });
		} else {
			return create(PolicyContentSchema, { content: { case: "url", value: policyUrlContent } });
		}
	}
</script>

<svelte:window onkeydown={handleKeyDown} />
<form class={twMerge("flex h-full flex-col overflow-y-auto px-2", className)}>
	{#if lastError}
		<Alert color="red" border class="mb-6 text-red-800">
			<div class="flex items-center">
				<Icon class="mr-2" name="exclamation-triangle" />
				{lastError}
			</div>
		</Alert>
	{/if}
	<div class="mb-auto flex flex-col gap-2 pb-6">
		<Label for="policy-name" class="required text-gray-900">Policy name</Label>
		<Input bind:elementRef={policyNameInput} {disabled} type="text" name="policy-name" id="policy-name" placeholder="Sweepstake T&Cs" required bind:value={policy.name} />
		<P secondary size="xs">This field is case sensitive and will be used as the display name.</P>

		<Label for="description" class="text-gray-900">Policy description</Label>
		<Input {disabled} type="text" class="" name="description" id="description" placeholder="T&Cs for the sweepstake campaign..." bind:value={policy.description} />

		<div class="mt-4">
			<div class="mb-2 flex flex-col">
				<Label class="text-md font-medium text-gray-900">Choose how to add your terms & conditions policy</Label>
				<P secondary size="xs">Link to an external policy document or webpage</P>
			</div>
			<div class="space-y-2">
				<div class="flex items-center gap-2">
					<Radio id="policyType-text" {disabled} name="policyType" value="text" bind:group={policyType} class="" />
					<Label for="policyType-text" class="flex flex-col">
						<P class="text-sm font-medium text-gray-900">Rich text editor</P>
						<P secondary size="xs">Write or paste your terms & conditions policy directly using the built-in editor</P>
					</Label>
				</div>
				<div class="flex items-center gap-2">
					<Radio id="policyType-url" {disabled} name="policyType" value="url" bind:group={policyType} class="" />
					<Label for="policyType-url" class="flex flex-col">
						<P class="text-sm font-medium text-gray-900">Link to a custom URL</P>
						<P secondary size="xs">Paste a link to an externally hosted terms & condtions policy</P>
					</Label>
				</div>
			</div>
		</div>

		<div class="mt-2">
			{#if policyType === "url"}
				<div class="mb-3 flex items-center justify-between">
					<Label for="url" class="text-gray-900">Add a terms & conditions URL</Label>
					<P secondary size="xs">Link to an external policy document or webpage</P>
				</div>
				<Input {disabled} type="url" name="url" id="url" placeholder="Type or paste a URL" required bind:value={policyUrlContent} />
				<P secondary class="mt-2" size="xs">Flowcode is not responsible for the content or enforceability of custom legal terms</P>
			{:else if policyType === "text"}
				<div class="mb-3 flex items-center justify-between">
					<Label for="text" class=" text-gray-900">Enter terms & conditions text below</Label>
					<P class="" secondary size="xs">Add a hyperlink anywhere within the text</P>
				</div>
				<HtmlEditor {disabled} initialContent={policyHtmlContent} onInput={handleTextContentChange} onChange={handleTextContentChange} />
				<div class="mt-2 flex items-center justify-between">
					<P secondary size="xs">Flowcode is not responsible for the content or enforceability of custom legal terms</P>
					<P secondary size="xs">{textInputCharCount}/{maxPolicyContentCharCount}</P>
				</div>
			{/if}
		</div>
	</div>
</form>
{#if showCreateAndUse}
	<div class="flex items-center justify-end gap-2 border-t px-6 py-4">
		<Button color="alternative" data-action-name="create-terms-and-conditions-policy-cancel" on:click={() => onClose()}>Cancel</Button>
		<Button data-action-name="policy-editor-create-and-use" disabled={submitDisabled} loading={isSubmitting} on:click={createPolicy}>Create and use policy</Button>
	</div>
{/if}
